/**
 * SponsorshipDeclineConfirm
 *
 * Citizen-facing confirmation for declining a sponsorship offer.
 * Decline auto-refunds the company in full, so the action needs an
 * explicit gate. Optional reason textarea — admin-visible only;
 * never travels to IRR payloads (PII).
 */

import { apiErrorCode, DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import { useState } from 'react';
import { useTranslation } from '@tonnie/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@tonnie/ui';
import { Textarea, Label } from '@tonnie/ui';
import { useDeclineSponsorshipOffer } from '@tonnie/sdk';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export interface SponsorshipDeclineConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sponsorshipId: string | null;
  company: string;
  /**
   * The journey is one the talent paid for: declining leaves it exactly as it
   * is, rather than on the basic tier.
   */
  keepsOwnAccess: boolean;
  onDeclined?: () => void;
}

export function SponsorshipDeclineConfirm({
  open,
  onOpenChange,
  sponsorshipId,
  company,
  keepsOwnAccess,
  onDeclined,
}: SponsorshipDeclineConfirmProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const decline = useDeclineSponsorshipOffer();

  const handleConfirm = async () => {
    if (!sponsorshipId) return;
    try {
      await decline.mutateAsync({
        sponsorshipId,
        reason: reason.trim().length > 0 ? reason.trim() : undefined,
      });
      toast.success(
        t('sponsorships.offer.declined.toast', {
          company,
        })
      );
      setReason('');
      onDeclined?.();
    } catch (err) {
      // Our words, never the server's: an offer that is no longer open (the
      // company cancelled it, or it was already answered) says so.
      toast.error(
        apiErrorCode(err) === DOMAIN_ERROR_CODES.SPONSORSHIP_NOT_AWAITING_TALENT
          ? t('sponsorships.offer.errors.notOpen')
          : t('sponsorships.offer.errors.decline')
      );
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('sponsorships.offer.decline.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {keepsOwnAccess
              ? t('sponsorships.offer.decline.bodyKeepsAccess', { company })
              : t('sponsorships.offer.decline.body', { company })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="decline-reason" className="text-sm">
            {t('sponsorships.offer.decline.reasonLabel', {
              company,
            })}
          </Label>
          <Textarea
            id="decline-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={2000}
            data-testid="sponsorship-decline-reason"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('sponsorships.offer.decline.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={decline.isPending || !sponsorshipId}
            className="bg-tone-rose-solid hover:bg-tone-rose-solid-hover text-tone-rose-on-solid"
            data-testid="sponsorship-decline-confirm"
          >
            {decline.isPending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" />
            ) : null}
            {t('sponsorships.offer.decline.confirmCta')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
